import { Alert, Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	Markdown,
	P,
	SimpleGrid,
	Stack,
	Text,
	useText,
	useTrackImpressions,
} from "@sveabank/cc-common";
import { isWithinInterval } from "date-fns";
import { isAfter } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router";
import Card from "../../components/card";
import Splash from "../../components/splash/splash";
import { usePageContext } from "../../hooks/context";
import { useDeleteApplicationMutation } from "../../services/application";
import { isFeatureEnabled } from "../../utils/feature-flags";
import ApplicationUnderReview from "./application-under-review";
import ClcPaymentModal from "./clc-payment-modal";
import { useDashboard } from "./hooks";
import IsCustomer from "./is-customer";
import NewApplicationModal from "./new-application-modal";
import { NewClcApplicationModal } from "./new-clc-application-modal";
import NonSignatorModal from "./non-signator-modal";
import NotCustomer from "./not-customer";
import OngoingApplication from "./ongoing-application";
import OngoingClcApplication from "./ongoing-clc";
import PreselectedPackageInfobox from "./preselected-package-infobox";
import { PromoCreditInterest } from "./promo-credit-interest";
import SetOrgNrModal from "./set-org-nr-modal";

const Applications = () => {
	const hasStartCompanyFeature = isFeatureEnabled("startCompany");

	const navigate = useNavigate();

	const [newApplicationId, setNewApplicationId] = useState<string>();

	const {
		promotions,
		inactiveEngagements,
		activeEngagements,
		applications,
		verifyNewApplication,
		nonVerifiedApplication,
		clcApplications,
	} = usePageContext(useDashboard);

	const [deleteApplicationMutation] = useDeleteApplicationMutation();

	useTrackImpressions(window.Matomo);

	const awaitingPaymentApplication = clcApplications.find(
		(application) => application.clcStatus === "AwaitingPayment",
	);

	const pendingClcApplication = clcApplications.find(
		(application) => application.clcStatus === "AwaitingOrgNumber",
	);

	const freeCertCampaignStart = new Date(2026, 8, 30, 18, 0);
	const hasCreatedAfterCampaignStart = clcApplications.every((application) =>
		isAfter(new Date(application.initiatedAt), freeCertCampaignStart),
	);

	const [isClcModalOpen, setIsClcModalOpen] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [nonSignatorModal, setNonSignatorModalOpen] = useState(false);
	const [clcPaymentModal, setClcPaymentModal] = useState(!!awaitingPaymentApplication);
	const [orgNrModal, setOrgNrModal] = useState(!!pendingClcApplication);

	const [t, texts] = useText();

	const applicationsUnderReview = applications.filter(
		(a) => a.applicationId !== nonVerifiedApplication && a.status === "OrderUnderReview",
	);

	const ongoingApplications = applications.filter(
		(a) => a.applicationId !== nonVerifiedApplication && a.status !== "OrderUnderReview",
	);

	const hasNoExistingCompanies =
		!inactiveEngagements.length && !applications.length && !activeEngagements.length;

	const canInitClcApplication =
		clcApplications.filter((a) => a.clcStatus !== "Cancelled" && a.clcStatus !== "Finalized")
			.length === 0;

	const today = new Date();
	const promoCodes = promotions
		.filter((p) =>
			isWithinInterval(today, {
				start: new Date(p.promotionStarts),
				end: new Date(p.promotionEnds),
			}),
		)
		.map((p) => p.promotion.promotionCode);

	return (
		<>
			<Stack gap={12}>
				<PreselectedPackageInfobox />
				{promoCodes.includes("Oct2026InterestRateDiscount") && <PromoCreditInterest />}

				<div className="pt-12">
					<SimpleGrid cols={hasStartCompanyFeature || !!clcApplications.length ? 2 : 1} gap={8}>
						<div className="relative w-full">
							{hasCreatedAfterCampaignStart && (
								<div className="absolute -top-11 right-5 z-20">
									<Splash />
								</div>
							)}
							<Card classNames="py-6">
								{(hasStartCompanyFeature || !!clcApplications.length) && (
									<Stack>
										{hasStartCompanyFeature && (
											<Stack gap={4} grow={false}>
												<Text component="h3" className="text-text-headline text-xl" bold>
													{t(texts.dashboardPage.newCompanyTitle)}
												</Text>
												<Stack gap={6}>
													<Markdown>{t(texts.dashboardPage.newCompanyDescription)}</Markdown>
													<Stack gap={4} grow={false} justify="start">
														{canInitClcApplication && (
															<ButtonGroup row align="start">
																<Button
																	onPress={() => setIsClcModalOpen(true)}
																	iconEnd="chevronRight"
																>
																	{t(texts.dashboardPage.newCompanyButtonText)}
																</Button>
															</ButtonGroup>
														)}
													</Stack>
												</Stack>
											</Stack>
										)}
										{clcApplications.length > 0 && (
											<Stack gap={2} grow={false}>
												{clcApplications.map((application) => (
													<OngoingClcApplication
														application={application}
														key={application.applicationId}
													/>
												))}
											</Stack>
										)}
									</Stack>
								)}
							</Card>
						</div>

						<Stack gap={4}>
							<Text component="h3" className="text-text-headline text-xl" bold>
								{t(texts.dashboardPage.existingCompanyTitle)}
							</Text>
							{hasNoExistingCompanies ? (
								<Stack
									data-track-content
									data-content-ignoreinteraction
									data-content-name="engagements"
									data-content-piece="noCompany"
								>
									<Alert kind="warning">
										{hasStartCompanyFeature
											? t(texts.dashboardPage.noExistingCompanies)
											: t(texts.dashboardPage.noExistingCompaniesNewCompanyDisabled)}
									</Alert>
								</Stack>
							) : (
								<>
									<P>{t(texts.dashboardPage.existingCompanyDescription)}</P>
									<Stack gap={2}>
										{ongoingApplications.length > 0 && (
											<Stack gap={4} grow={false}>
												{ongoingApplications.map((application) => (
													<OngoingApplication
														application={application}
														key={application.applicationId}
													/>
												))}
											</Stack>
										)}
										{inactiveEngagements.length > 0 && (
											<Stack
												gap={4}
												grow={false}
												data-track-content
												data-content-ignoreinteraction
												data-content-name="engagements"
												data-content-piece="newCompany"
											>
												{inactiveEngagements.map((engagement) => (
													<NotCustomer
														engagement={engagement}
														setIsNewApplicationModalOpen={setModalOpen}
														setNewApplicationId={setNewApplicationId}
														setNonSignatorModalOpen={setNonSignatorModalOpen}
														key={engagement.orgNr}
														withPromos={promoCodes}
													/>
												))}
											</Stack>
										)}
										{applicationsUnderReview.length > 0 && (
											<Stack
												gap={4}
												grow={false}
												data-track-content
												data-content-ignoreinteraction
												data-content-name="engagements"
												data-content-piece="reviewedCompany"
											>
												{applicationsUnderReview.map((application) => (
													<ApplicationUnderReview
														application={application}
														key={application.applicationId}
													/>
												))}
											</Stack>
										)}
										{activeEngagements.length > 0 && (
											<Stack
												gap={4}
												grow={false}
												data-track-content
												data-content-ignoreinteraction
												data-content-name="engagements"
												data-content-piece="registeredCompany"
											>
												{activeEngagements.map((engagement) => (
													<IsCustomer engagement={engagement} key={engagement.orgNr} />
												))}
											</Stack>
										)}
									</Stack>
								</>
							)}
						</Stack>
					</SimpleGrid>
				</div>
			</Stack>

			<NewClcApplicationModal closeModal={() => setIsClcModalOpen(false)} isOpen={isClcModalOpen} />
			<NewApplicationModal
				isOpen={modalOpen}
				onClose={async () => {
					if (newApplicationId) {
						await deleteApplicationMutation(newApplicationId);
					}
					setModalOpen(false);
				}}
				onSubmit={() => {
					verifyNewApplication();
					navigate(`/choose-package/${newApplicationId}`);
				}}
				applicationId={newApplicationId}
			/>

			<NonSignatorModal isOpen={nonSignatorModal} setOpen={setNonSignatorModalOpen} />
			{awaitingPaymentApplication && (
				<ClcPaymentModal
					isOpen={clcPaymentModal}
					setOpen={setClcPaymentModal}
					applicationId={awaitingPaymentApplication.applicationId}
				/>
			)}
			{pendingClcApplication && (
				<SetOrgNrModal
					isOpen={orgNrModal}
					setOpen={setOrgNrModal}
					pendingClcApplication={pendingClcApplication}
				/>
			)}
		</>
	);
};

export default Applications;
